import CloseIcon from '@assets/icons/close.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { groupSortMultiMatchResults } from '@/utils/pack-sort';
import { ItemsContext_VariantsPackFieldsFragment } from '@/types';
import { KeyboardAwareRecyclerList } from '@/components/recycler-list';
import { PackUnit } from '@/enums';
import { ScannerType } from './scanner-types';
import { ScanResultListItem } from './scan-result-list-item';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useScannerContext } from '@/contexts/scanner-context';
import { View } from 'react-native';

// Groups matches by product (multiple products can appear together in
// fuzzy/partial name-match mode) before sorting each product's packs. See
// groupSortMultiMatchResults for the auto_select_lowest_uom behavior, shared
// with the local-scan chooser in local-scanner-context.
const useGroupSortedResults = () => {
  const { autoSelectLowestUom, packSortConfig } = useItemDisplayOptions();

  return (
    items: ItemsContext_VariantsPackFieldsFragment[],
  ): ItemsContext_VariantsPackFieldsFragment[] =>
    groupSortMultiMatchResults(
      items,
      (vp) => vp.item.uuid,
      (vp) => ({
        displayString: vp.item.display_name,
        id: vp.uuid,
        unit: vp.pack.unit as PackUnit,
        unitCount: vp.pack.unit_count,
      }),
      autoSelectLowestUom,
      packSortConfig,
    );
};

interface Props {
  scannerType: ScannerType;
}

export const ScanResults: React.FC<Props> = ({ scannerType }) => {
  const [styles, theme] = useStyles();

  const {
    cartVariantPacks,
    offDeviceSearchResults,
    onDeviceSearchResults,
    updateCartWithMultiUpdates,
  } = useScannerContext();
  const groupSortResults = useGroupSortedResults();

  const searchResultsSection = useMemo(() => {
    const searchResults =
      (offDeviceSearchResults ?? onDeviceSearchResults) || [];
    const cartVariantPackUUIDs = new Set(
      cartVariantPacks?.map((vp) => vp.uuid),
    );
    const initialAccumulator: {
      inCart: ItemsContext_VariantsPackFieldsFragment[];
      notInCart: ItemsContext_VariantsPackFieldsFragment[];
    } = { inCart: [], notInCart: [] };
    const { inCart, notInCart } = searchResults.reduce((acc, item) => {
      if (cartVariantPackUUIDs.has(item.uuid)) {
        acc.inCart.push(item);
      } else {
        acc.notInCart.push(item);
      }
      return acc;
    }, initialAccumulator);

    return [
      {
        data: [...groupSortResults(inCart), ...groupSortResults(notInCart)],
        key: i18n.t('results_from_catalog'),
      },
    ];
  }, [
    cartVariantPacks,
    groupSortResults,
    offDeviceSearchResults,
    onDeviceSearchResults,
  ]);

  return (
    <View
      style={[
        styles.flex,
        scannerType === ScannerType.External ? styles.listMargin : null,
      ]}
    >
      <KeyboardAwareRecyclerList
        alwaysBounceVertical={false}
        estimatedItemSize={80}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        listItemProps={{
          listItemStyle: styles.listItem,
          renderMainContent: (vp) => <ScanResultListItem variantPack={vp} />,
        }}
        renderSectionHeader={(item) => (
          <View style={styles.sectionHeader}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-md"
            >
              {item.title}
            </Typography>
            {scannerType === ScannerType.Camera ? (
              <Button
                Icon={CloseIcon}
                iconProps={{
                  stroke: theme.sk.color.icon.primary,
                }}
                onPress={updateCartWithMultiUpdates}
                rounded
                size="small"
                style={styles.clearButton}
                trackingData={null}
                type="tertiary"
              />
            ) : null}
          </View>
        )}
        sections={searchResultsSection}
        stickyHeaderIndices={[]}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  clearButton: {
    backgroundColor: theme.sk.color.bg.tertiary.hover,
    height: 32,
    width: 32,
  },
  flex: {
    flex: 1,
  },
  listItem: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  listMargin: {
    marginVertical: 24,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 16,
  },
}));
